import { TranslationKey } from 'modules/i18n';

/**
 * Single source of truth for the CV-builder wizard's shape: order, wire slug, whether the
 * step's data is mandatory, and whether it counts toward progress.
 *
 * Before this table the same facts were spelled out in four places — `PROGRESS_CONFIG.
 * STEP_WEIGHTS`, the `progressSteps` array literal inside `useStepValidation`, the
 * next/previous chain in the same file, and `guest-cv/resumeStep.DATA_STEP_SLUGS` — which
 * meant adding or reordering a step required four synchronized edits.
 *
 * ## The slugs are a backend contract
 * `slug` is the wire value. It MUST stay byte-identical to `CvBuilderStep.key` in
 * pro-core-service: the guest autosave sends these in `completedSteps`, and
 * `CvBuilderStep.fromKey` (a `@JsonCreator`) THROWS on an unknown key — so a renamed or
 * invented slug turns every `PUT /guest/curriculumVitaes` into a 400. Frontend keys are
 * ours to change; slugs are not. `__tests__/stepMeta.test.ts` pins the list.
 *
 * ## `requiresData` mirrors the server, it does not extend it
 * True for exactly the steps pro-core marks `requiresData` (personal details, education,
 * language skills) and hard-gates in `CvService.assertSubmittable`. Promoting an optional
 * step here would block submits the backend would happily accept.
 *
 * ## `countsTowardProgress` is the operator funnel's denominator
 * pro-core's `AccessGrantService.recordProgress` computes the grant's `progressPercent`
 * over the steps IT marks `countsTowardProgress` (7 of 10). Changing which steps we
 * acknowledge would move a number operators read, so this mirror must not drift.
 */
export type StepKey =
  | 'LANGUAGE'
  | 'OVERVIEW'
  | 'PERSONAL_DETAILS'
  | 'EDUCATION'
  | 'PROFESSIONAL_EXPERIENCE'
  | 'HARD_SKILLS'
  | 'SOFT_SKILLS'
  | 'LANGUAGE_SKILLS'
  | 'CERTIFICATES'
  | 'SUMMARY';

export interface StepMeta {
  key: StepKey;
  /** Wire value — must equal pro-core `CvBuilderStep.key`. Also the `?step=` param value. */
  slug: string;
  /** Short step name, used by the step header and the summary rows. */
  labelKey: TranslationKey;
  /**
   * The step reached by "Back". Deliberately absent for the first three steps, which is
   * the pre-existing behavior: the wizard never navigates back into the intro/overview.
   * `PreviousStepNavigationButton` renders nothing when this is undefined.
   */
  previousKey?: StepKey;
  /** Data is mandatory for submit — mirrors pro-core `CvBuilderStep.requiresData`. */
  requiresData: boolean;
  /** Contributes to the progress denominator — mirrors pro-core `countsTowardProgress`. */
  countsTowardProgress: boolean;
}

/** Wizard order. "Next" is the following entry; `previousKey` is explicit (see above). */
export const CV_BUILDER_STEP_META: readonly StepMeta[] = [
  {
    key: 'LANGUAGE',
    slug: 'language',
    labelKey: 'cvBuilderPage.intro.heading',
    requiresData: false,
    countsTowardProgress: false
  },
  {
    key: 'OVERVIEW',
    slug: 'overview',
    labelKey: 'cvBuilderPage.overview.progress.title',
    requiresData: false,
    countsTowardProgress: false
  },
  {
    key: 'PERSONAL_DETAILS',
    slug: 'personal-details',
    labelKey: 'cvBuilderPage.overview.personalDetails.title',
    requiresData: true,
    countsTowardProgress: true
  },
  {
    key: 'EDUCATION',
    slug: 'education',
    labelKey: 'cvBuilderPage.overview.education.title',
    previousKey: 'PERSONAL_DETAILS',
    requiresData: true,
    countsTowardProgress: true
  },
  {
    key: 'PROFESSIONAL_EXPERIENCE',
    slug: 'professional-experience',
    labelKey: 'cvBuilderPage.overview.professionalExperience.title',
    previousKey: 'EDUCATION',
    requiresData: false,
    countsTowardProgress: true
  },
  {
    key: 'HARD_SKILLS',
    slug: 'hard-skills',
    labelKey: 'cvBuilderPage.overview.hardSkills.title',
    previousKey: 'PROFESSIONAL_EXPERIENCE',
    requiresData: false,
    countsTowardProgress: true
  },
  {
    key: 'SOFT_SKILLS',
    slug: 'soft-skills',
    labelKey: 'cvBuilderPage.overview.softSkills.title',
    previousKey: 'HARD_SKILLS',
    requiresData: false,
    countsTowardProgress: true
  },
  {
    key: 'LANGUAGE_SKILLS',
    slug: 'language-skills',
    labelKey: 'cvBuilderPage.overview.languageSkills.title',
    previousKey: 'SOFT_SKILLS',
    requiresData: true,
    countsTowardProgress: true
  },
  {
    key: 'CERTIFICATES',
    slug: 'certificates',
    labelKey: 'cvBuilderPage.overview.certificates.title',
    previousKey: 'LANGUAGE_SKILLS',
    requiresData: false,
    countsTowardProgress: true
  },
  {
    key: 'SUMMARY',
    slug: 'summary',
    labelKey: 'cvBuilderPage.summary.title',
    previousKey: 'CERTIFICATES',
    requiresData: false,
    countsTowardProgress: false
  }
];

const BY_KEY = new Map<StepKey, StepMeta>(
  CV_BUILDER_STEP_META.map(step => [step.key, step])
);

const BY_SLUG = new Map<string, StepMeta>(
  CV_BUILDER_STEP_META.map(step => [step.slug, step])
);

/** The 7 progress-contributing steps, in wizard order. */
export const PROGRESS_STEPS: readonly StepMeta[] = CV_BUILDER_STEP_META.filter(
  step => step.countsTowardProgress
);

/** The keys of the 7 progress-contributing steps — the acknowledgement set. */
export const PROGRESS_STEP_KEYS: readonly StepKey[] = PROGRESS_STEPS.map(
  step => step.key
);

export const getStep = (key: StepKey): StepMeta => {
  const step = BY_KEY.get(key);
  if (!step) {
    throw new Error(`Unknown CV builder step key: ${key}`);
  }
  return step;
};

/** Undefined for an unrecognized slug (e.g. a hand-edited `?step=`), never throws. */
export const getStepBySlug = (slug: string | null | undefined) =>
  slug ? BY_SLUG.get(slug) : undefined;

/** The next step in wizard order, or undefined for the terminal step. */
export const getNextStep = (key: StepKey): StepMeta | undefined => {
  const index = CV_BUILDER_STEP_META.findIndex(step => step.key === key);
  return index === -1 ? undefined : CV_BUILDER_STEP_META[index + 1];
};

export const getPreviousStep = (key: StepKey): StepMeta | undefined => {
  const previousKey = BY_KEY.get(key)?.previousKey;
  return previousKey ? BY_KEY.get(previousKey) : undefined;
};

/**
 * 1-based position among the progress-contributing steps, for "Step 3 of 7". Undefined
 * for the navigation-only steps (language / overview / summary), which are not numbered.
 */
export const getProgressPosition = (key: StepKey): number | undefined => {
  const index = PROGRESS_STEPS.findIndex(step => step.key === key);
  return index === -1 ? undefined : index + 1;
};

export const isProgressStepKey = (key: string): key is StepKey =>
  PROGRESS_STEP_KEYS.includes(key as StepKey);
